import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useAtom, useAtomValue } from "jotai";
import playSound from "@/sound";
import { wordPopState } from "@stores/popup";
import SwiperCore, { Pagination, Navigation } from "swiper/core";
import { Swiper, SwiperSlide } from "swiper/react";
import PopupCloseButton from "@components/elements/PopupCloseButton";
import RotateCard from "../components/modules/RoateCard";
import { currentPageState } from "../stores/vars";

SwiperCore.use([Pagination, Navigation]);

const Wrap = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: #ffcc00;
  z-index: 105;
  animation: fullDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const CardContainer = styled.div`
  position: relative;
  width: 1500px;
  height: 100%;
  transform: translateX(-50%);
  left: 50%;

  &.mode2 {
    .card-box {
      width: 1400px;
      font-size: 95px;
    }
    .rCard {
      .rbtn {
        right: -30px;
      }
    }
  }
`;

const DirectionButton = styled.button`
  position: absolute;
  width: 186px;
  top: 50%;
  margin-top: -75px;
  height: 158px;
  &.next {
    background: url(${require("@assets/img/word_pop_next.png")});
    right: -1px;
  }

  &.prev {
    background: url(${require("@assets/img/word_pop_prev.png")});
    left: -1px;
  }

  &.swiper-button-disabled {
    opacity: 0.2;
  }
  transition: opacity 0.4s;
`;

const ButtonWrap = styled.div`
  height: 155px;
  background-color: #fff;
  border-radius: 75px;
  position: absolute;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  padding: 0 30px;
  width: auto;
`;

const WordButton = styled.button`
  width: 390px;
  height: 112px;
  background-color: #2196f3;
  border-radius: 70px;
  font-size: 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-right: 30px;
  &:last-child {
    margin-right: 0;
  }
  &:before {
    content: "";
    display: inline-block;
    width: 51px;
    height: 47px;
    margin-right: 15px;
  }
  &.ch {
    background-color: #2ebdac;
    &:before {
      background: url(${require("@assets/img/word_button_ch.png")});
      background-size: 100%;
    }
  }
  &.ch2 {
    background-color: #009cff;
    &:before {
      background: url(${require("@assets/img/word_button_ch2.png")});
      background-size: 100%;
    }
  }
  &.ko {
    background-color: #f26622;
    &:before {
      background: url(${require("@assets/img/word_button_ko.png")});
      background-size: 100%;
    }
  }

  &.on {
    border: 10px solid rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    //pointer-events: none;
  }
`;

const ChangeModeButton = styled.button`
  position: absolute;
  right: 50px;
  top: 50px;
  width: 320px;
  height: 124px;
  background: url(${require("@assets/img/word_swap.png")}) no-repeat;
  &.on {
    background: url(${require("@assets/img/word_swap2.png")}) no-repeat;
  }
  &.ty2 {
    background: url(${require("@assets/img/word_swap_ty2_2.png")}) no-repeat;
    &.on {
      background: url(${require("@assets/img/word_swap_ty2.png")}) no-repeat;
    }
  }
`;

const WordPopup = () => {
  const [wordInfo, setWordPop] = useAtom(wordPopState);
  const currentPage = useAtomValue(currentPageState);
  const [curWordData, setWordData] = useState({
    curMode: wordInfo.mode,
    data: wordInfo.data.length === 0 ? wordInfo.data2 : wordInfo.data,
    data2: wordInfo.data2,
  });

  const [count, setCount] = useState(0);
  const cards = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);

  const onClose = () => {
    setWordPop({
      open: false,
      data: [],
      data2: [],
    });
  };

  const renderSlide = useMemo(() => {
    return curWordData.data.map((d, i) => {
      return (
        <SwiperSlide key={i}>
          {d.img === "null" ? (
            <RotateCard
              img={d.img}
              sound={d.sound}
              cName={"rCard"}
              flipCard={(isFront, index) => {
                if (!isFront) {
                  activeCurrentWordButton(null);
                } else {
                  activeCurrentWordButton(null, index);
                }
              }}
              direction={
                curWordData.curMode === "word" ? "horizontal" : "vertical"
              }
            />
          ) : (
            <RotateCard
              img={d.img}
              sound={d.sound}
              cName={"rCard"}
              flipCard={(isFront, index) => {
                if (!isFront) {
                  activeCurrentWordButton(null);
                } else {
                  activeCurrentWordButton(null, index);
                }
              }}
              direction={
                curWordData.curMode === "word" ? "horizontal" : "vertical"
              }
            />
          )}
        </SwiperSlide>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curWordData.data]);

  const activeCurrentWordButton = (target, idx = -1) => {
    const buttons = document.querySelectorAll(".word-button");
    buttons.forEach((el) => el.classList.remove("on"));
    if (!target) {
      if (idx > -1) {
        if (buttons[idx]) buttons[idx].classList.add("on");
      }
    } else {
      target.classList.add("on");
    }
  };

  const renderButtons = () => {
    const { buttons } = curWordData.data[count];
    return buttons.map((b, i) => {
      const key = Object.keys(b)[0];
      const value = b[key];
      const buttonLabel =
        key === "ko"
          ? "한글로 보기"
          : key === "ch2"
          ? "병음으로 보기"
          : "한자로 보기";
      return (
        <WordButton
          type="button"
          data-name={value}
          data-index={i}
          className={`word-button ${key}`}
          key={i}
          onClick={(e) => {
            if (e.target.classList.contains("on")) {
              if (curWordData.data[0].img === "null") return;
              activeCurrentWordButton(null, -1);
              cards.current[swiperRef.current.activeIndex].resetCard("_keep");
            } else {
              const currentClickWord = value;
              onClickWord(currentClickWord, i);
              activeCurrentWordButton(e.target);
            }
          }}
        >
          {buttonLabel}
        </WordButton>
      );
    });
  };

  const onSlideChange = (swiper) => {
    activeCurrentWordButton(null, -1);
  };

  const slideChangeTransitionEnd = (swiper) => {
    const activeIndex = swiper.activeIndex;
    setCount(activeIndex);
    cards.current.forEach((el, i) => {
      const value = Object.values(curWordData.data[i].buttons[0])[0];
      el.resetCard(value);
    });
  };

  const onClickWord = (word, currentIndex) => {
    cards.current[count].rotateCard(word, currentIndex);
  };

  const onClickModeChange = () => {
    if (curWordData.curMode === "word") {
      setWordData({
        curMode: "word2",
        data: wordInfo.data2,
      });
    } else {
      setWordData({
        curMode: "word",
        data: wordInfo.data,
      });
    }
    swiperRef.current.slideTo(0, 0);
  };

  const getType = () => {
    if (curWordData.curMode === "word") {
      return "";
    } else {
      return "on";
    }
  };

  useEffect(() => {
    cards.current = document.querySelectorAll(".rCard");
    cards.current.forEach((el, i) => {
      const value = Object.values(curWordData.data[i].buttons[0])[0];
      el.resetCard(value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordInfo.open, curWordData.data]);

  useEffect(() => {
    document.body.classList.add("has-popup");
    playSound.bgm(require("@assets/sounds/bgm/word.mp3"));
    return () => {
      playSound.wordStop();
      if (currentPage === "main") {
        playSound.bgm(require("@assets/sounds/bgm/main.mp3"));
      } else if (currentPage === "book") {
        playSound.bgm(require("@assets/sounds/bgm/book.mp3"));
      }
      document.body.classList.remove("has-popup");
    };
  }, []);

  return (
    <Wrap>
      <PopupCloseButton onClick={onClose} />
      <DirectionButton className="prev" type="button" ref={prevRef} />
      <DirectionButton className="next" type="button" ref={nextRef} />
      <CardContainer
        className={
          curWordData.data[0].img === "null"
            ? ""
            : curWordData.curMode !== "word" && "mode2"
        }
      >
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          spaceBetween={200}
          onSlideChange={onSlideChange}
          onSlideChangeTransitionEnd={slideChangeTransitionEnd}
          pagination={{ clickable: true }}
          navigation={{
            prevEl: prevRef.current ? prevRef.current : undefined,
            nextEl: nextRef.current ? nextRef.current : undefined,
          }}
        >
          {renderSlide}
        </Swiper>
      </CardContainer>
      <ButtonWrap>{renderButtons()}</ButtonWrap>
      {wordInfo.data.length && wordInfo.data2.length > 0 && (
        <ChangeModeButton
          onClick={onClickModeChange}
          className={`${
            curWordData.data[0].img === "null" && "ty2"
          } ${getType()}`}
        />
      )}
    </Wrap>
  );
};
export default React.memo(WordPopup);
